import {useCallback, useEffect, useState, useRef} from "react";
import {useKeycloakFunctions} from "./useKeycloakFunctions";
import {customerProductsAPI} from "../common/api/customerProductsAPI";
import {createURLQueryFromParams} from "../common/api/apiUtil";
import {
    API_RESIDENCE_POINTS,
    API_RESIDENCE_POINTS_FOR_SALE,
    API_RESIDENCE_POINTS_MOVEMENTS,
    API_RESIDENCE_POINTS_NOT_AVAILABLE,
    API_RESIDENCE_POINTS_SUMMARY,
    API_RESIDENCE_POINTS_TRANSFERS
} from "../common/api/apiConstants";

export const useResidencePoints = () => {

    const {initialized, authenticated} = useKeycloakFunctions();

    const [pointsAvailable, setPointsAvailable] = useState();
    const [loadingPointsAvailable, setLoadingPointsAvailable] = useState(false);
    const [errorPointsAvailable, setErrorPointsAvailable] = useState(false);

    const [pointsAvailableByValidity, setPointsAvailableByValidity] = useState();
    const [loadingPointsAvailableByValidity, setLoadingPointsAvailableByValidity] = useState(false);
    const [errorPointsAvailableByValidity, setErrorPointsAvailableByValidity] = useState(false);

    const [residencePointsForSale, setResidencePointsForSale] = useState(undefined);
    const [loadingResidencePointsForSale, setLoadingResidencePointsForSale] = useState(false);
    const [errorResidencePointsForSale, setErrorResidencePointsForSale] = useState(false);

    const [pointsMovements, setPointsMovements] = useState();
    const [pointsMovementsLimit, setPointsMovementsLimit] = useState(null); // Internal use only
    const [loadingPointsMovements, setLoadingPointsMovements] = useState(false);
    const [errorPointsMovements, setErrorPointsMovements] = useState();

    const [pointsNotAvailable, setPointsNotAvailable] = useState();
    const [loadingPointsNotAvailable, setLoadingPointsNotAvailable] = useState(false);
    const [errorPointsNotAvailable, setErrorPointsNotAvailable] = useState(false);

    const [pointsTransfer, setPointsTransfer] = useState();
    const [loadingPointsTransfer, setLoadingPointsTransfer] = useState(false);
    const [errorPointsTransfer, setErrorPointsTransfer] = useState(null);

    const shouldUpdate = useRef(true);

    useEffect(() => {
        return () => {
            shouldUpdate.current = false;
        };
    }, []);

    const getPointsAvailable = (hapimag_member_id) => {
        if (initialized && authenticated) {
            setLoadingPointsAvailable(true);
            setErrorPointsAvailable(null);

            const params = {};
            if (hapimag_member_id) {
                params.hapimag_member_id = hapimag_member_id;
            }

            customerProductsAPI.get(API_RESIDENCE_POINTS + "/" + createURLQueryFromParams(params))
                .then(res => res.data)
                .then(response => {
                    if (shouldUpdate.current) {
                        setLoadingPointsAvailable(false);
                        setPointsAvailable(response);
                    }
                })
                .catch(err => {
                    if (shouldUpdate.current) {
                        setLoadingPointsAvailable(false);
                        setErrorPointsAvailable(err);
                    }
                });
        }
    };

    const getPointsAvailableByValidity = (hapimag_member_id) => {
        if (initialized && authenticated) {
            setLoadingPointsAvailableByValidity(true);
            setErrorPointsAvailableByValidity(null);

            const params = {};
            if (hapimag_member_id) {
                params.hapimag_member_id = hapimag_member_id;
            }

            customerProductsAPI.get(API_RESIDENCE_POINTS + API_RESIDENCE_POINTS_SUMMARY + createURLQueryFromParams(params))
                .then(res => res.data)
                .then(response => {
                    if (shouldUpdate.current) {
                        setLoadingPointsAvailableByValidity(false);
                        setPointsAvailableByValidity(response);
                    }
                })
                .catch(err => {
                    if (shouldUpdate.current) {
                        setLoadingPointsAvailableByValidity(false);
                        setErrorPointsAvailableByValidity(err);
                    }
                });
        }
    };

    const getResidencePointsForSale = (hapimag_member_id) => {
        if (initialized && authenticated) {
            setLoadingResidencePointsForSale(true);
            setErrorResidencePointsForSale(null);

            const params = {};
            if (hapimag_member_id) {
                params.hapimag_member_id = hapimag_member_id;
            }

            customerProductsAPI.get(API_RESIDENCE_POINTS + API_RESIDENCE_POINTS_FOR_SALE + createURLQueryFromParams(params))
                .then(res => res.data)
                .then(response => {
                    if (shouldUpdate.current) {
                        setLoadingResidencePointsForSale(false);
                        setResidencePointsForSale(response);
                    }
                })
                .catch(err => {
                    if (shouldUpdate.current) {
                        setLoadingResidencePointsForSale(false);
                        setErrorResidencePointsForSale(err);
                    }
                });
        }
    };

    const getPointsMovements = ({locale, limit}, hapimag_member_id) => {
        if (initialized && authenticated && limit !== pointsMovementsLimit) {
            const params = {
                locale,
                limit: limit || undefined
            };
            if (hapimag_member_id) {
                params.hapimag_member_id = hapimag_member_id;
            }

            setLoadingPointsMovements(true);
            setErrorPointsMovements(null);

            customerProductsAPI.get(API_RESIDENCE_POINTS + API_RESIDENCE_POINTS_MOVEMENTS + createURLQueryFromParams(params))
                .then(res => res.data)
                .then(response => {
                    if (shouldUpdate.current) {
                        setLoadingPointsMovements(false);
                        setPointsMovements(response?.points_movement);
                        setPointsMovementsLimit(limit);
                    }
                })
                .catch(err => {
                    if (shouldUpdate.current) {
                        setLoadingPointsMovements(false);
                        setErrorPointsMovements(err);
                    }
                });
        }
    };

    const getPointsNotAvailable = (hapimag_member_id) => {
        if (initialized && authenticated) {
            setLoadingPointsNotAvailable(true);
            setErrorPointsNotAvailable(null);

            const params = {};
            if (hapimag_member_id) {
                params.hapimag_member_id = hapimag_member_id;
            }

            return customerProductsAPI.get(API_RESIDENCE_POINTS + API_RESIDENCE_POINTS_NOT_AVAILABLE + createURLQueryFromParams(params))
                .then(res => {
                    if (shouldUpdate.current) {
                        // Calculate points total
                        const highSeasonPointsTotal = res?.data?.reduce((accumulator, object) => {
                            return accumulator + object.high_season_points;
                        }, 0);

                        const lowSeasonPointsTotal = res?.data?.reduce((accumulator, object) => {
                            return accumulator + object.low_season_points;
                        }, 0);

                        setPointsNotAvailable({
                            points: res?.data,
                            highSeasonPointsTotal,
                            lowSeasonPointsTotal
                        });
                        return res;
                    }
                    else {
                        return new Promise(resolve => resolve());
                    }
                })
                .catch(err => {
                    if (shouldUpdate.current) {
                        setErrorPointsNotAvailable(err);
                        throw err.response;
                    }
                })
                .finally(() => {
                    setLoadingPointsNotAvailable(false);
                });
        }
    };

    const doPointsTransfer = useCallback((pointsExpirationDate, points, transferTo) => {
        if (initialized && authenticated) {
            setLoadingPointsTransfer(true);
            setErrorPointsTransfer(null);

            const body = {
                residence_points_expiration_date: pointsExpirationDate,
                residence_points_amount: points,
                transfer_to_member_id: transferTo
            };

            return customerProductsAPI.post(API_RESIDENCE_POINTS + API_RESIDENCE_POINTS_TRANSFERS, body)
                .then(res => {
                    if (res?.status === 200) {
                        setPointsTransfer(true);
                    }
                    return res;
                })
                .catch(err => {
                    setErrorPointsTransfer(err?.response);
                    throw err.response;
                })
                .finally(() => {
                    setLoadingPointsTransfer(false);
                });
        } else {
            return new Promise(resolve => resolve());
        }
    }, [initialized, authenticated]);

    const reasonsMapper = useCallback((points, type, metadata = null) => {
        const reasons = {
            "yearly_opening_balance": "view.points_shares.points.transactions.your_points_as_at",
            "credit_by_share_purchase": "view.points_shares.points.transactions.credit_by_share_purchase",
            "manual_points_booking": points > 0 ? "view.points_shares.points.transactions.manual_points_credit" : "view.points_shares.points.transactions.manual_points_debit",
            "booking": "view.points_shares.points.transactions.booking",
            "points_purchase_or_sales_on_old_points_kiosk": points > 0 ? "view.points_shares.points.transactions.points_purchase" : "view.points_shares.points.transactions.points_sale",
            "points_transfer": (!metadata || (metadata.first_name && metadata.last_name)) ? (points > 0 ? "view.points_shares.points.transactions.transferred_from" : "view.points_shares.points.transactions.transferred_to") : "view.points_shares.shares.product.transfer",
            "points_transfer_from_marketplace_purchase": (!metadata || (metadata.first_name && metadata.last_name)) ? (points > 0 ? "view.points_shares.points.transactions.transferred_from" : "view.points_shares.points.transactions.transferred_to") : "view.points_shares.shares.product.transfer",
            "points_credit_from_insurance": "view.points_shares.points.transactions.from_insurance",
            "yearly_points_credit": "view.points_shares.points.transactions.yearly_points_credit",
            "points_expiration": "view.points_shares.points.transactions.expired_points",
            "residence": "view.points_shares.points.transactions.residence",
            "storno_due_to_share_purchase": "view.points_shares.points.transactions.share_purchase_cancellation",
            "points_lost_due_to_cancellation": "view.points_shares.points.transactions.cancellation",
            "credit_from_referral_program": "view.points_shares.points.transactions.referral_credit",
            "charge_due_to_repurchase": "view.points_shares.points.transactions.share_repurchase",
            "charge_to_to_contract_termination": "view.points_shares.points.transactions.contract_termination",
            "charge_due_to_loyality_program": "view.points_shares.points.transactions.loyalty_programm_credit",
            "credit_due_to_promotion": "view.points_shares.points.transactions.promotion_credit",
            "scrapping_of_points": "view.points_shares.points.transactions.scrapping_lost",
            "credit_from_subscription": "view.points_shares.points.transactions.subscription_credit",
            "cancelled_subscription": "view.points_shares.points.transactions.cancelled_subscription",
            "credit_due_to_loyality_program": "view.points_shares.points.transactions.from_loyalty_programm",
            "points_purchase_or_sales_on_new_points_platform": points > 0 ? "view.points_shares.points.transactions.points_purchase" : "view.points_shares.points.transactions.points_sale",
        };

        return reasons[type];
    }, []);

    return {
        getPointsAvailable,
        pointsAvailable,
        loadingPointsAvailable,
        errorPointsAvailable,

        getPointsAvailableByValidity,
        pointsAvailableByValidity,
        loadingPointsAvailableByValidity,
        errorPointsAvailableByValidity,

        getResidencePointsForSale,
        residencePointsForSale,
        loadingResidencePointsForSale,
        errorResidencePointsForSale,

        getPointsMovements,
        pointsMovements,
        loadingPointsMovements,
        errorPointsMovements,

        pointsNotAvailable,
        getPointsNotAvailable,
        loadingPointsNotAvailable,
        errorPointsNotAvailable,

        pointsTransfer,
        loadingPointsTransfer,
        errorPointsTransfer,
        doPointsTransfer,

        reasonsMapper
    };
};
